import type { TemplateInput, FooterLink as ServiceFooterLink, FooterSocialLink as ServiceFooterSocialLink, NavLink as ServiceNavLink } from '@amzn/mulberry-internal-typescript-client';
import type { HeaderProps, FooterProps, TopPromoBannerProps, FooterLink, FooterSocialLink, NavBarLink } from '@amzn/burberry-ui-components';

const FOOTER_LINK_TYPE_MAP: Record<string, 'tos' | 'pp' | 'faq'> = {
  TERMS_OF_SERVICE: 'tos',
  PRIVACY_POLICY: 'pp',
  FREQUENTLY_ASKED_QUESTIONS: 'faq',
};

const SOCIAL_PLATFORM_MAP: Record<string, FooterSocialLink['platform']> = {
  INSTAGRAM: 'instagram',
  TIKTOK: 'tiktok',
  X: 'x',
  FACEBOOK: 'facebook',
  YOUTUBE: 'youtube',
};

const mapNavBarLinks = (links?: ServiceNavLink[]): NavBarLink[] | undefined => {
  if (!links) return undefined;
  return links
    .filter((link) => link.linkText && link.linkUrl)
    .map((link) => ({
      linkText: link.linkText!,
      linkUrl: link.linkUrl!,
    }));
};

const mapFooterLinks = (links?: ServiceFooterLink[]): FooterLink[] | undefined => {
  if (!links) return undefined;
  return links
    .filter((link) => link.type && FOOTER_LINK_TYPE_MAP[link.type])
    .map((link) => ({
      type: FOOTER_LINK_TYPE_MAP[link.type!],
      url: link.url,
      text: link.text ?? '',
    }));
};

const mapFooterSocialLinks = (links?: ServiceFooterSocialLink[]): FooterSocialLink[] | undefined => {
  if (!links) return undefined;
  return links
    .filter((link) => link.platform && SOCIAL_PLATFORM_MAP[link.platform])
    .map((link) => ({
      platform: SOCIAL_PLATFORM_MAP[link.platform!],
      url: link.url ?? '',
    }));
};

export const getHeaderProps = (template: TemplateInput): Omit<HeaderProps, 'metricEmitter'> => ({
  navBarPrimaryText: template.navBarPrimaryText,
  navBarMainImage: template.navBarMainImage,
  navBarMainLink: template.navBarMainLink,
  navBarLeftImage: template.navBarLeftImage,
  navBarLeftLink: template.navBarLeftLink,
  navBarBackgroundImage: template.navBarBackgroundImage,
  navBarHeight: template.navBarHeight,
  navBarLinks: mapNavBarLinks(template.navBarLinks),
});

export const getFooterProps = (template: TemplateInput): Omit<FooterProps, 'metricEmitter'> => ({
  footerPrimaryText: template.footerPrimaryText,
  footerSocialLinks: mapFooterSocialLinks(template.footerSocialLinks),
  footerLinks: mapFooterLinks(template.footerLinks),
  showAccessibilitySupport: template.showAccessibilitySupport,
  footerBanner: template.footerBanner,
});

export const getTopPromoBannerProps = (template: TemplateInput): Omit<TopPromoBannerProps, 'metricEmitter'> => ({
  topPromoBannerText: template.topPromoBannerText,
  topPromoBannerBackgroundColor: template.topPromoBannerBackgroundColor,
  topPromoBannerBackgroundImage: template.topPromoBannerBackgroundImage,
  topPromoBannerHeight: template.topPromoBannerHeight,
});
